import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Button from '../Button';
import Typography from '../Typography';
import ChevronLeftIcon from '../Icons/ChevronLeftIcon';
import ChevronRightIcon from '../Icons/ChevronRightIcon';
import { parseStoredDate } from './datePickerValue';
import { getCalendarWeeks, isDayAfterMax, isInViewMonth, isSameCalendarDay } from './datePickerCalendarUtils';
import { getThemeStyleFromAnchor } from './datePickerPortalTheme';
import './DatePickerPopover.scss';

const DROPDOWN_GAP = 8;
const PANEL_WIDTH = 328;

function ChevronNav({ direction, label, onClick }) {
  return (
    <Button
      variant="tertiary"
      size="sm"
      className="ds-date-picker__nav-btn"
      aria-label={label}
      onClick={onClick}
      icon={direction === 'prev' ? <ChevronLeftIcon size={18} /> : <ChevronRightIcon size={18} />}
    />
  );
}

ChevronNav.propTypes = {
  direction: PropTypes.oneOf(['prev', 'next']).isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default function DatePickerPopover({
  open,
  onDismiss,
  onApply,
  onClear,
  value,
  maxDate: maxDateProp,
  anchorRef,
  overlayClassName = '',
}) {
  const intl = useIntl();
  const max = maxDateProp === undefined ? moment().endOf('day') : maxDateProp;
  const panelRef = useRef(null);

  const [pendingDate, setPendingDate] = useState(() => parseStoredDate(value));
  const [viewMonth, setViewMonth] = useState(() => {
    const parsed = parseStoredDate(value);
    return parsed.isValid() ? parsed.clone().startOf('month') : moment().startOf('month');
  });
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0 });
  const [overlayThemeStyle, setOverlayThemeStyle] = useState(undefined);

  useEffect(() => {
    if (!open) return;
    const parsed = parseStoredDate(value);
    setPendingDate(parsed);
    setViewMonth(parsed.isValid() ? parsed.clone().startOf('month') : moment().startOf('month'));
  }, [open, value]);

  const updatePanelPosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const { top: anchorTop, left: anchorLeft, bottom: anchorBottom } = el.getBoundingClientRect();
    let top = anchorBottom + DROPDOWN_GAP;
    let left = anchorLeft;

    if (panelRef.current) {
      const estH = panelRef.current.offsetHeight || 380;
      if (top + estH > window.innerHeight - 12 && anchorTop > estH + DROPDOWN_GAP) {
        top = anchorTop - estH - DROPDOWN_GAP;
      }
    }

    const maxLeft = window.innerWidth - PANEL_WIDTH - 12;
    if (left > maxLeft) left = Math.max(12, maxLeft);
    if (left < 12) left = 12;

    setPanelStyle({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    setOverlayThemeStyle(getThemeStyleFromAnchor(anchorRef?.current));
    updatePanelPosition();
    const onScroll = () => updatePanelPosition();
    const onResize = () => updatePanelPosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePanelPosition, pendingDate, viewMonth]);

  useEffect(() => {
    if (!open || !onDismiss) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  const weekDayLabels = useMemo(() => {
    const raw = intl.locale || 'en';
    const locKey = raw.replace('_', '-').split('-')[0];
    try {
      const long = moment.localeData(locKey).weekdays();
      const min = moment.localeData(locKey).weekdaysMin();
      return min.map((short, index) => ({
        key: long[index],
        label: short.length > 2 ? short.slice(0, 2).toUpperCase() : short.toUpperCase(),
      }));
    } catch {
      return [
        { key: 'Sunday', label: 'SU' },
        { key: 'Monday', label: 'MO' },
        { key: 'Tuesday', label: 'TU' },
        { key: 'Wednesday', label: 'WE' },
        { key: 'Thursday', label: 'TH' },
        { key: 'Friday', label: 'FR' },
        { key: 'Saturday', label: 'SA' },
      ];
    }
  }, [intl.locale]);

  const formatMonthTitle = useCallback(
    (month) => intl.formatDate(month.toDate(), { month: 'long', year: 'numeric' }),
    [intl]
  );

  const weeks = useMemo(() => getCalendarWeeks(viewMonth), [viewMonth]);

  const handleDayClick = useCallback(
    (day) => {
      if (isDayAfterMax(day, max)) return;
      setPendingDate(day.clone().startOf('day'));
    },
    [max]
  );

  const canApply = pendingDate.isValid();

  const handleDone = useCallback(() => {
    if (!canApply) return;
    onApply(pendingDate.clone().startOf('day'));
    onDismiss();
  }, [canApply, onApply, onDismiss, pendingDate]);

  const hasStoredValue = parseStoredDate(value).isValid();

  const handleClear = useCallback(
    (event) => {
      event?.preventDefault?.();
      onClear?.(event);
      onDismiss();
    },
    [onClear, onDismiss]
  );

  const shiftViewMonth = useCallback((delta) => {
    setViewMonth((month) => month.clone().add(delta, 'month'));
  }, []);

  if (!open) return null;

  const overlayClass = ['ds-date-picker__overlay', overlayClassName].filter(Boolean).join(' ');

  return ReactDOM.createPortal(
    <div className={overlayClass} style={overlayThemeStyle}>
      <button
        type="button"
        className="ds-date-picker__backdrop"
        aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
        onMouseDown={(event) => {
          event.preventDefault();
          onDismiss();
        }}
      />
      <div
        ref={panelRef}
        className="ds-date-picker"
        style={{
          position: 'fixed',
          top: panelStyle.top,
          left: panelStyle.left,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage({ id: 'select-date', defaultMessage: 'Select Date' })}
      >
        <div className="ds-date-picker__calendar">
          <div className="ds-date-picker__month-nav">
            <ChevronNav
              direction="prev"
              label={intl.formatMessage({
                id: 'client-connect-calendar-prev-month',
                defaultMessage: 'Previous month',
              })}
              onClick={() => shiftViewMonth(-1)}
            />
            <Typography variant="bodyMedium" component="span" className="ds-date-picker__month-title">
              {formatMonthTitle(viewMonth)}
            </Typography>
            <ChevronNav
              direction="next"
              label={intl.formatMessage({
                id: 'client-connect-calendar-next-month',
                defaultMessage: 'Next month',
              })}
              onClick={() => shiftViewMonth(1)}
            />
          </div>
          <div className="ds-date-picker__dow-row">
            {weekDayLabels.map(({ key, label }) => (
              <Typography key={key} component="span" variant="bodySmall" className="ds-date-picker__dow-cell">
                {label}
              </Typography>
            ))}
          </div>
          <div className="ds-date-picker__weeks">
            {weeks.map((row) => (
              <div key={String(row[0].valueOf())} className="ds-date-picker__week-row">
                {row.map((day) => {
                  const inMonth = isInViewMonth(day, viewMonth);
                  const disabled = isDayAfterMax(day, max);
                  const selected = pendingDate.isValid() && isSameCalendarDay(day, pendingDate);

                  return (
                    <Button
                      key={day.valueOf()}
                      variant="tertiary"
                      size="sm"
                      disabled={disabled}
                      className={[
                        'ds-date-picker__day-cell',
                        !inMonth && 'ds-date-picker__day-cell--outside',
                        selected && 'ds-date-picker__day-cell--selected',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleDayClick(day)}
                    >
                      <Typography variant="bodyMedium" component="span" className="ds-date-picker__day-num">
                        {day.date()}
                      </Typography>
                    </Button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="ds-date-picker__footer">
          {hasStoredValue && onClear ? (
            <Button
              variant="link"
              size="sm"
              className="ds-date-picker__clear-btn"
              onClick={handleClear}
            >
              {intl.formatMessage({
                id: 'clear',
                defaultMessage: 'Clear',
              })}
            </Button>
          ) : (
            <Typography variant="bodySmall" component="p" className="ds-date-picker__hint">
              {intl.formatMessage({
                id: 'ds-date-picker-hint',
                defaultMessage: 'Select a date',
              })}
            </Typography>
          )}
          <Button
            variant="primary"
            size="sm"
            className="ds-date-picker__done-btn"
            disabled={!canApply}
            onClick={handleDone}
          >
            {intl.formatMessage({
              id: 'ds-date-picker-done',
              defaultMessage: 'Done',
            })}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

DatePickerPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  value: PropTypes.string,
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.oneOf([null])]),
  anchorRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.instanceOf(typeof HTMLElement !== 'undefined' ? HTMLElement : Object),
      PropTypes.oneOf([null]),
    ]),
  }),
  overlayClassName: PropTypes.string,
};

DatePickerPopover.defaultProps = {
  onClear: undefined,
  value: '',
  maxDate: undefined,
  anchorRef: null,
  overlayClassName: '',
};
